import bind from 'bind-decorator';
import { createPopper } from '@popperjs/core/lib/popper-lite';
import popperPreventOverflow from '@popperjs/core/lib/modifiers/preventOverflow';
import popperOffset from '@popperjs/core/lib/modifiers/offset';

import View from './view';
import globalHandler from '@/helpers/global_handler';
import showModal from '@/utils/show_modal';
import OptionList from './custom_select/option_list';
import { dropdownMaxHeight } from './custom_select/dropdown_max_height';

// A dropdown drawn in place of a native <select>: the select stays the state and the form
// field, this view adds the placeholder, the dropdown chrome, the popper positioning and the
// keyboard. The options themselves are rendered by OptionList — the single producer of that
// markup, shared with the lists rendered open elsewhere.
//
// Input handles, all read off the markup this view is built over:
//   .b-input-v4 on the node — the v4 design: html options, no arrow icon
//   .disabled on the node, or a disabled select — no dropdown and no tabindex
//   data-is_html_options — render the content of an <option> as html (v4 always does)
//   data-is_dynamic_dropdown_height — inside a fixed container (a modal) clamp the dropdown
//     to the space left below the placeholder
//   class and data attributes of an <option> — moved onto the option drawn for it
//   data-divider on an <option> — becomes the is-group-start class
//   .placeholder-inner-text inside an <option> — the part of it the placeholder shows alone
//
// Produced markup: .custom-select-placeholder > .placeholder-inner and
// .custom-select-dropdown > button.option[data-value], carrying is-current and
// is-group-start. Those class names and the transfer of the data attributes are a contract
// with the consumers: vue components style the rendered options from their scoped styles
// through ::v-deep, which works only because the data-v-* scope id of an <option> travels
// onto the option drawn for it.
//
// Lifecycle: every initialized instance registers itself here and a single module-level
// turbolinks:before-cache handler destroys all of them. A consumer that outlives a
// navigation-less rerender (the order column of boxes_show, a vue modal) destroys its own
// instances on top of that — destroy is idempotent, so the two never collide.
const instances = new Set();
let isBeforeCacheBound = false;

function register(instance) {
  instances.add(instance);

  if (isBeforeCacheBound) { return; }

  isBeforeCacheBound = true;
  $(document).on('turbolinks:before-cache', destroyAll);
}

function destroyAll() {
  [...instances].forEach(instance => instance.destroy());
}

export default class CustomSelect extends View {
  isFocused = false;
  prevValue = null;
  popper = null;

  initialize() {
    if (!this.isV4) {
      this.$node.addClass('custom-select');
    }

    this.$select = this.$('select');

    this.renderDropdown();
    this.renderPlaceholder();
    this.$dropdown.insertAfter(this.$placeholder);

    if (!this.isDisabled) {
      this.modal = showModal({
        $modal: this.$dropdown,
        $trigger: this.$placeholder,
        $outerNode: this.$node,
        show: this.onShow,
        hide: this.onHide,
        isHiddenCheck: () => !this.isFocused,
        clickEventName: 'mousedown'
      });
    }

    register(this);
  }

  get isV4() {
    return this.node.classList.contains('b-input-v4');
  }

  get isHtml() {
    return this.isV4 || this.node.hasAttribute('data-is_html_options');
  }

  get isDynamicDropdownHeight() {
    return this.node.hasAttribute('data-is_dynamic_dropdown_height');
  }

  get currentValue() {
    return this.$select.val();
  }

  get $label() {
    return this.$('label');
  }

  get isDisabled() {
    return this.$node.hasClass('disabled') ||
      this.$select.is(':disabled');
  }

  @bind
  onShow() {
    this.isFocused = true;

    requestAnimationFrame(() => {
      this.$node.addClass('is-focused');
      this.$placeholder.addClass('is-focused');
    });
    this.$dropdown.removeClass('hidden');

    const dropdownOffset = 4;
    const dropdownPadding = 10;

    if (this.isDynamicDropdownHeight) {
      const maxHeight = this.calculateDropdownMaxHeight(dropdownOffset, dropdownPadding);
      if (maxHeight) {
        this.$dropdown.css('max-height', `${maxHeight}px`);
      }
    }

    this.popper = createPopper(
      this.$placeholder[0],
      this.$dropdown[0],
      {
        placement: 'bottom-start',
        modifiers: [popperPreventOverflow, popperOffset, {
          name: 'preventOverflow',
          options: { padding: dropdownPadding }
        }, {
          name: 'offset',
          options: { offset: [0, dropdownOffset] }
        }, {
          name: 'sameWidth',
          enabled: true,
          phase: 'beforeWrite',
          requires: ['computeStyles'],
          fn: ({ state }) => {
            state.styles.popper.width = `${state.rects.reference.width}px`;
          },
          effect: ({ state }) => {
            state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
          }
        }]
      }
    );

    globalHandler
      .on('up', this.onPressUp)
      .on('down', this.onPressDown)
      .on('enter', this.onPressEnter);

    this.prevValue = this.currentValue;
  }

  @bind
  onHide() {
    this.isFocused = false;

    requestAnimationFrame(() => {
      this.$node.removeClass('is-focused');
      this.$placeholder.removeClass('is-focused');
    });
    this.$dropdown.addClass('hidden');
    // the next open measures the natural height of the content again from scratch
    this.$dropdown.css('max-height', '');

    if (this.popper) {
      this.popper.destroy();
      this.popper = null;
    }

    globalHandler
      .off('up', this.onPressUp)
      .off('down', this.onPressDown)
      .off('enter', this.onPressEnter);

    if (this.prevValue !== this.currentValue) {
      this.$node.trigger('custom-select:change');
    }
  }

  @bind
  onOptionClick() {
    this.$dropdown.trigger('modal:hide');
  }

  @bind
  onPressEnter() {
    this.$dropdown.trigger('modal:hide');
  }

  @bind
  onPressUp(e) {
    const $newOption = this.$('.option.is-current').first().prevAll('.option').first();
    if (!$newOption.length) { return; }

    e.preventDefault();
    e.stopImmediatePropagation();

    this.selectOption($newOption[0].getAttribute('data-value'));
  }

  @bind
  onPressDown(e) {
    const $newOption = this.$('.option.is-current').last().nextAll('.option').first();
    if (!$newOption.length) { return; }

    e.preventDefault();
    e.stopImmediatePropagation();

    this.selectOption($newOption[0].getAttribute('data-value'));
  }

  // idempotent: a vue consumer destroys in onBeforeUnmount and the registry may destroy the
  // same instance again on the next turbolinks:before-cache
  @bind
  destroy() {
    if (!this.optionList) { return; }

    instances.delete(this);

    if (this.popper) {
      this.popper.destroy();
      this.popper = null;
    }
    // the delegated handler sits on the node, which the view did not create and does not
    // remove — without this it outlives the dropdown it was bound for
    this.off('click', '.custom-select-dropdown .option', this.onOptionClick);
    this.optionList.destroy();
    this.optionList = null;
    this.$placeholder.remove();
    this.$dropdown.remove();
    this.modal?.destroy();
    this.modal = null;
  }

  renderPlaceholder() {
    this.$placeholder = $(`
      <div class="custom-select-placeholder">
        <div class="placeholder-inner"></div>
` + (
      this.isV4 ?
        '' :
        `<div class="b-svg-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
            <g clip-path="url(#clip0_112_999)">
              <path d="M3.68784 1.09443L0.127804 4.65453C0.045398 4.73687 0 4.84679 0 4.96399C0 5.08119 0.045398 5.19111 0.127804 5.27345L0.389915 5.53563C0.560711 5.70623 0.838302 5.70623 1.00884 5.53563L3.99834 2.54612L6.99116 5.53894C7.07357 5.62128 7.18342 5.66675 7.30056 5.66675C7.41782 5.66675 7.52768 5.62128 7.61015 5.53894L7.87219 5.27677C7.9546 5.19436 8 5.08451 8 4.96731C8 4.8501 7.9546 4.74019 7.87219 4.65785L4.30891 1.09443C4.22624 1.01189 4.11587 0.966558 3.99854 0.966818C3.88075 0.966558 3.77044 1.01189 3.68784 1.09443Z" fill="currentColor"/>
            </g>
            <defs>
              <clipPath id="clip0_112_999">
                <rect width="8" height="8" fill="white"/>
              </clipPath>
            </defs>
          </svg>
        </div>`) + `
      </div>
    `);

    if (this.isDisabled) {
      this.$placeholder.addClass('is-disabled');
      if (!this.isV4) {
        this.$placeholder.find('.b-svg-icon').addClass('is-disabled');
      }
    } else {
      this.$placeholder.prop('tabindex', '0');
    }

    this.syncPlaceholder();

    this.$placeholder.insertAfter(this.$label.length ? this.$label : this.$select);
  }

  renderDropdown() {
    this.$dropdown = $(`
      <div class="custom-select-dropdown hidden" data-scroll-lock-scrollable></div>
    `);

    this.optionList = new OptionList({
      $select: this.$select,
      $container: this.$dropdown,
      isHtml: this.isHtml,
      onSelect: this.syncPlaceholder
    });

    this.on('click', '.custom-select-dropdown .option', this.onOptionClick);
  }

  selectOption(value) {
    this.optionList.selectOption(value);
  }

  @bind
  syncPlaceholder() {
    this.$placeholder
      .find('.placeholder-inner')
      .replaceWith(this.optionList.renderCurrentOption($('<div class="placeholder-inner" />')));
  }

  calculateDropdownMaxHeight(dropdownOffset, dropdownPadding) {
    const placeholder = this.$placeholder[0];

    if (!this.isInsideFixedContainer(placeholder)) {
      return null;
    }

    return dropdownMaxHeight({
      // viewport coordinates on both sides: inside a fixed container the page scroll
      // does not move the placeholder, so a document-relative top would be compared
      // against a viewport height and collapse the dropdown to its floor
      placeholderBottom: placeholder.getBoundingClientRect().bottom,
      viewportHeight: window.visualViewport?.height || window.innerHeight,
      // scrollHeight, not clientHeight: the latter is already cut by the max-height
      // of a previous open, so the dropdown could only ever get smaller
      contentHeight: this.$dropdown[0].scrollHeight,
      offset: dropdownOffset,
      padding: dropdownPadding
    });
  }

  isInsideFixedContainer(element) {
    let current = element;

    while (current && current !== document.body) {
      const position = window.getComputedStyle(current).position;

      if (position === 'fixed') {
        return true;
      }
      current = current.parentElement;
    }

    return false;
  }
}
